import Vue from 'vue';
import { mapGetters } from 'vuex';
import Query from 'src/api/query';
import QueryUtils from 'src/utils/query';
import { BfButton } from 'components/Butterfly';
import { calculateNPS, calculateNPSImpact, calculateOtherNPS } from 'src/utils/nps';
import FormatUtils from 'src/utils/formatters';
export default Vue.extend({
    components: { BfButton },
    props: {
        clickable: { type: Boolean, default: true },
        title: {
            type: String,
            default: 'Net Promoter Score (NPS)'
        },
        queryResultCount: { type: Number, required: false, default: null },
        query: { type: Object, default: null },
    },
    data() {
        return {
            showingRawData: false,
            categories: { promoters: 'promoters', passives: 'passives', detractors: 'detractors' },
            queryNpsFreq: null,
            waiting: 0
        };
    },
    computed: {
        ...mapGetters(['featureFlags', 'currentProject', 'hasNPS', 'currentModel', 'nps', 'savedQueries']),
        overallNpsScores() {
            return this.nps ? calculateNPS(this.nps.detractors, this.nps.passives, this.nps.promoters, true) : null;
        },
        npsScores() {
            // if we have a query & it's NPS freq
            if (!!this.query && !!this.queryNpsFreq) {
                return calculateNPS(this.queryNpsFreq.detractors, this.queryNpsFreq.passives, this.queryNpsFreq.promoters, true);
            }
            return this.overallNpsScores;
        },
        otherNpsScores() {
            return this.nps && this.currentModel && this.queryNpsFreq ?
                calculateOtherNPS({
                    ...this.nps,
                    totalCount: this.currentModel.stats.n_documents,
                }, {
                    ...this.queryNpsFreq,
                    totalCount: this.queryResultCount,
                }, true) : null;
        },
        impactScore() {
            return this.nps && this.currentModel && this.queryNpsFreq ?
                calculateNPSImpact({
                    ...this.nps,
                    totalCount: this.currentModel.stats.n_documents,
                }, {
                    ...this.queryNpsFreq,
                    totalCount: this.queryResultCount,
                }, true) : null;
        },
        loading() {
            return this.waiting > 0;
        },
        queriedNpsTypes() {
            if (!this.query) {
                return [];
            }
            let rows = QueryUtils.botanicToQueryRows(this.query);
            let field = rows.find((v) => v.type === 'segment' && v.field.toLowerCase() === 'nps category');
            return field ? field.values.map((v) => v.toLowerCase() + 's') // Convert from query to statistics key
                : [];
        }
    },
    watch: {
        query: function (val) {
            this.$nextTick(async () => {
                if (this.hasNPS === true) {
                    await this.getQueryNPS(this.query);
                }
            });
        }
    },
    async mounted() {
        if (this.hasNPS === true && this.query) {
            await this.getQueryNPS(this.query);
        }
    },
    methods: {
        capitalize: FormatUtils.capitalize,
        formatNPS: FormatUtils.formatNPS,
        async getQueryNPS(query) {
            try {
                this.waiting += 1;
                const response = await Query.getNpsData(this.$route.params.projectId, this.$route.params.analysisId, query, this.savedQueries);
                this.queryNpsFreq = {
                    detractors: response.detractors.total_hits,
                    passives: response.passives.total_hits,
                    promoters: response.promoters.total_hits
                };
            }
            catch (e) {
                this.queryNpsFreq = null;
            }
            finally {
                this.waiting -= 1;
            }
        },
        calcDifference(type = 'nps') {
            if (!this.npsScores || !this.overallNpsScores)
                return;
            let v = (this.npsScores[type] - this.overallNpsScores[type]).toFixed(2);
            if (v > 0) {
                v = `+${v}`;
            }
            if (type !== 'nps') {
                v = `${v}%`;
            }
            return v;
        },
        // Should the specified nps `type` be clickable?
        isClickable(type) {
            if (!this.npsScores) {
                return false;
            }
            return this.clickable && this.npsScores[type] > 0 && !this.queriedNpsTypes.includes(type);
        },
        // Emit event indicating clicking of nps `type` in the visualization.
        selectNps(type) {
            if (this.isClickable(type)) {
                type = type[0].toUpperCase() + type.substring(1, type.length - 1); // Convert from statistics to query key
                this.$emit('nps-clicked', type);
            }
        }
    }
});
